import * as React from 'react';
import type { Theme } from '@emotion/react';
import { css } from '@emotion/react';
import type { ElementTypeLean, MergeFirst } from '@resi-media/resi-ui';
import { ListItem, mergeDefaultProps } from '@resi-media/resi-ui';

const defaultElement = 'li';

type _Props<T extends ElementTypeLean = typeof defaultElement> = MergeFirst<ListItem.Props<T>, object> & {
  as?: T;
};

const S = {
  container: (theme: Theme) =>
    css(
      `background-color: ${theme.palette.surface.canvas};

     :hover:not(:disabled, &[data-${theme.prefix}-button-base--disabled]),
     :active:not(:disabled, &[data-${theme.prefix}-button-base--disabled]),
        &[aria-pressed='true']:not(:disabled, &[data-${theme.prefix}-button-base--disabled]) {
        background-color: ${theme.palette.surface.disabled};
        box-shadow: none;
      }
    `
    ),
};

const defaultProps = {
  as: 'li' as ElementTypeLean,
};

type _InternalProps = MergeFirst<_Props, typeof defaultProps>;

const SearchListItemInternal = <T extends ElementTypeLean = typeof defaultElement>(
  props: _Props<T>,
  ref: React.ForwardedRef<HTMLElement>
) => {
  const propsInternal: _InternalProps = mergeDefaultProps(props, defaultProps);
  const { as, ...rest } = propsInternal;

  return (
    <ListItem<typeof as>
      ref={ref}
      as={as}
      color="text.title"
      css={S.container}
      fontSize="page"
      height="x10"
      px="x4"
      py="s"
      whiteSpace="normal"
      {...rest}
    />
  );
};

SearchListItemInternal.displayName = 'SearchListItem';

/* eslint-disable import/export */
export const SearchListItem = Object.assign(React.forwardRef(SearchListItemInternal), {
  displayName: 'SearchListItem',
});

// eslint-disable-next-line no-redeclare
export namespace SearchListItem {
  export type Props<T extends ElementTypeLean = typeof defaultElement> = _Props<T>;
}
